/**
 * Named photo slots for the QC Standard (Combined) photo-submission section.
 *
 * The QC Standard form collects a fixed set of named photos, grouped into a
 * sensible walkthrough order: exterior → interior → mechanicals → lockbox
 * close-out. Each slot maps to a `mediaType` string stored on the visit-form
 * media record; existing pre-walk mediaTypes are reused where they already
 * exist so downstream tooling stays consistent.
 *
 * `minFiles` defaults to 0 (optional). Bump a slot's `minFiles` to make it
 * required — the matching validation lives in `utils/validation.ts`.
 *
 * A slot with `conditionField`/`conditionValue` is only shown (and only
 * required) when `formData[conditionField] === conditionValue`. If the slot
 * also sets `conditionLabel`, a Yes/No gate question is rendered inline right
 * before the photo (e.g. "Does the home have a garage?"). Without a
 * `conditionLabel`, the gate is answered elsewhere (e.g. the For Sale Sign
 * photo keys off the exterior section's "valid sign installed?" answer).
 */

export type PhotoGroup = 'exterior' | 'interior' | 'mechanical' | 'lockbox';

export interface PhotoSlot {
  mediaType: string;
  label: string;
  group: PhotoGroup;
  minFiles?: number;
  maxFiles?: number;
  helpText?: string;
  conditionField?: string;
  conditionValue?: string;
  conditionLabel?: string;
}

/** Display order + headings for the photo groups. */
export const PHOTO_GROUPS: { key: PhotoGroup; heading: string }[] = [
  { key: 'exterior', heading: 'Exterior Photos' },
  { key: 'interior', heading: 'Interior Photos' },
  { key: 'mechanical', heading: 'Mechanicals & Utilities' },
  { key: 'lockbox', heading: 'Before You Leave' },
];

/** Guidance shown on the required interior-room photo slots. */
const ROOM_PHOTO_HELP =
  'Take at least 5 wide shots from different corners of the room so the full space is visible in each photo — avoid tight close-ups.';

export const QC_COMBINED_PHOTO_SLOTS: PhotoSlot[] = [
  // ── Exterior ─────────────────────────────────────────────────────────────
  { group: 'exterior', mediaType: 'photo_street_front', label: 'Street View of Property Front', minFiles: 1, maxFiles: 1, helpText: 'Wide shot from the street showing the full front of the home.' },
  { group: 'exterior', mediaType: 'photo_side_1', label: 'Side View 1', minFiles: 1, maxFiles: 5, helpText: 'The left side of the home, front to back.' },
  { group: 'exterior', mediaType: 'photo_side_2', label: 'Side View 2', minFiles: 1, maxFiles: 5, helpText: 'The right side of the home, front to back.' },
  { group: 'exterior', mediaType: 'photo_address_verification', label: 'Address Verification', minFiles: 1, maxFiles: 5, helpText: 'Close-up of the house number or address marker.' },
  { group: 'exterior', mediaType: 'photo_street_scene', label: 'Street Scene', minFiles: 1, maxFiles: 5, helpText: 'View down the street showing the surrounding block.' },
  { group: 'exterior', mediaType: 'photo_back_yard', label: 'Back Yard', minFiles: 1, maxFiles: 10, helpText: 'Wide shot of the rear yard and the back of the home.' },
  { group: 'exterior', mediaType: 'photo_for_sale_sign', label: 'For Sale Sign', minFiles: 1, maxFiles: 5, conditionField: 'sign_status', conditionValue: 'Yes', helpText: 'The installed sign and rider, with the text legible.' },
  { group: 'exterior', mediaType: 'photo_garage', label: 'Garage', minFiles: 1, maxFiles: 10, conditionField: 'has_garage', conditionValue: 'Yes', conditionLabel: 'Does the home have a garage?', helpText: 'The garage door and interior.' },
  { group: 'exterior', mediaType: 'photo_pool', label: 'Pool', minFiles: 1, maxFiles: 10, conditionField: 'has_pool', conditionValue: 'Yes', conditionLabel: 'Does the home have a pool?', helpText: 'Full view of the pool and surrounding deck.' },
  { group: 'exterior', mediaType: 'photo_hvac_exterior', label: 'Exterior Mechanical / HVAC Unit', minFiles: 1, maxFiles: 10, helpText: 'The outdoor condenser / HVAC unit.' },
  // ── Interior rooms (at least 5 wide photos each) ─────────────────────────
  { group: 'interior', mediaType: 'photo_kitchen', label: 'Kitchen', minFiles: 5, maxFiles: 20, helpText: ROOM_PHOTO_HELP },
  { group: 'interior', mediaType: 'photos_living_areas', label: 'Living Room', minFiles: 5, maxFiles: 20, helpText: ROOM_PHOTO_HELP },
  { group: 'interior', mediaType: 'photos_bedrooms', label: 'Bedrooms', minFiles: 5, maxFiles: 20, helpText: ROOM_PHOTO_HELP },
  { group: 'interior', mediaType: 'photos_bathrooms', label: 'Bathrooms', minFiles: 5, maxFiles: 20, helpText: ROOM_PHOTO_HELP },
  { group: 'interior', mediaType: 'photo_laundry_room', label: 'Basement', minFiles: 1, maxFiles: 10, conditionField: 'has_basement', conditionValue: 'Yes', conditionLabel: 'Does the home have a basement?', helpText: 'Wide shots of the basement space.' },
  // ── Mechanicals — close-up photos of the unit labels ─────────────────────
  { group: 'mechanical', mediaType: 'photo_furnace_label', label: 'Furnace / HVAC Unit (close-up of label)', minFiles: 1, maxFiles: 5, helpText: 'Close-up of the furnace / HVAC data label showing model and serial number.' },
  { group: 'mechanical', mediaType: 'photo_water_heater_label', label: 'Hot Water Tank (close-up of label)', minFiles: 1, maxFiles: 5, helpText: 'Close-up of the water heater data label showing model and serial number.' },
  { group: 'mechanical', mediaType: 'photo_electrical_panel_label', label: 'Electrical Panel (close-up of label)', minFiles: 1, maxFiles: 5, helpText: 'Open the panel door and capture the breaker labels.' },
  { group: 'mechanical', mediaType: 'photo_thermostat', label: 'Thermostat (showing current reading and settings)', minFiles: 1, maxFiles: 5, helpText: 'Close-up clearly showing the current temperature reading and mode.' },
  // ── Lockbox close-out — taken after returning the key and scrambling the code ──
  {
    group: 'lockbox',
    mediaType: 'photo_lockbox_key_returned',
    label: 'Key returned to the lockbox',
    minFiles: 1,
    maxFiles: 2,
    helpText: 'After the visit, make sure all doors are locked, put the key back in the lockbox, and close and secure it. Take a photo of the key back in the lockbox.',
  },
  {
    group: 'lockbox',
    mediaType: 'photo_lockbox_code_scrambled',
    label: 'Lockbox code scrambled',
    minFiles: 1,
    maxFiles: 2,
    helpText: 'Take a photo showing the lockbox numbers have been adjusted and the code is no longer in place. Leaving the code in place puts the home at extreme risk for burglaries and trespassers.',
  },
];
